export interface KeyboardNavInput {
  key: string;
  /** Cmd (macOS) — jumps to the first/last row with the arrow keys. */
  metaKey: boolean;
  /** Ctrl — same edge-jump behavior as Cmd. */
  ctrlKey: boolean;
  /** Index of the currently-selected row, or -1 when nothing is selected. */
  currentIndex: number;
  rowCount: number;
  /** Rows moved per PageUp/PageDown. */
  pageJump: number;
}

/**
 * Resolve the destination row index for a navigation key press, matching the
 * prior AG-grid keyboard behavior (`gridKeyboardNavigation.ts`): arrows move by
 * one (clamped), Cmd/Ctrl+arrow jumps to an edge, Home/End jump to first/last,
 * and PageUp/PageDown move by `pageJump`. From an empty selection an arrow/page
 * key lands on the first row.
 *
 * Returns the target index, or `null` when `key` isn't a navigation key (Enter
 * and Space are activation, handled by the caller) or the grid is empty.
 */
export function resolveKeyboardNavTarget({
  key,
  metaKey,
  ctrlKey,
  currentIndex,
  rowCount,
  pageJump,
}: KeyboardNavInput): number | null {
  if (rowCount <= 0) return null;
  const last = rowCount - 1;
  const toEdge = metaKey || ctrlKey;

  switch (key) {
    case "ArrowUp":
      if (toEdge) return 0;
      return currentIndex === -1 ? 0 : Math.max(0, currentIndex - 1);
    case "ArrowDown":
      if (toEdge) return last;
      return currentIndex === -1 ? 0 : Math.min(last, currentIndex + 1);
    case "Home":
      return 0;
    case "End":
      return last;
    case "PageUp":
      return currentIndex === -1 ? 0 : Math.max(0, currentIndex - pageJump);
    case "PageDown":
      return currentIndex === -1 ? 0 : Math.min(last, currentIndex + pageJump);
    default:
      return null;
  }
}
